import { useState, useEffect } from 'react';

type Destructor = () => void;
type UseSharedStateReturnType<T> = [T | undefined, (v: T) => void];

// create a shared value that can be referenced with the returned hook
export const createSharedState = <T>(initialValue?: T) => {
  const emitter = createEmitter<T>();

  const useSharedState = (): UseSharedStateReturnType<T> => {
    const [value, setValue] = useState<T>(initialValue as T);

    // subscribe to global changes
    useEffect(() => emitter.subscribe(setValue), []);

    return [
      value,
      (v: T) => {
        emitter.emit(v);
        setValue(v);
      },
    ];
  };
  useSharedState.emitter = emitter;

  return useSharedState;
};

const createEmitter = <T>() => {
  let value = null as T;
  const subscriptions = new Map();
  return {
    emit: (v: T) => {
      if (value === v) return;
      value = v;
      subscriptions.forEach((fn) => fn(v));
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    subscribe: (fn: (v: T) => any): Destructor => {
      const key = Symbol();
      subscriptions.set(key, fn);
      return () => subscriptions.delete(key);
    },
    value,
  };
};
